import classNames from 'classnames';
import { IconV1 } from 'common/icon/iconV1.js';
import * as React from 'react';
import { useMeasureNew } from 'utilities/hooks/use-measure-new.js';

interface IAccordionItemProps {
  heading: React.ReactNode;
  headingAdditional: React.ReactNode;
  body: React.ReactNode;
  bodyClassName?: string;
  toggleCallback: (index: number) => void;
  isOpen: boolean;
  index: number;
  warningSeverityClass?: string;
}

const getRootMinMaxHeight = ({
  headDom,
  bodyDom,
  isOpen,
}: {
  headDom?: DOMRect;
  bodyDom?: DOMRect;
  isOpen: boolean;
}): [React.CSSProperties['minHeight'], React.CSSProperties['maxHeight']] => {
  const headDomHeight = headDom?.height ?? 0 + (headDom?.y ?? 0);
  const bodyDomHeight = bodyDom?.height ?? 0 + (bodyDom?.y ?? 0);
  if (isOpen) {
    return [`${headDomHeight}px`, `${bodyDomHeight + headDomHeight}px`];
  }
  return [`${headDomHeight}px`, `${headDomHeight}px`];
};

export const AccordionItemNew = ({
  heading,
  headingAdditional,
  body,
  isOpen,
  warningSeverityClass,
  index,
  toggleCallback,
}: IAccordionItemProps) => {
  const headRef = React.useRef<HTMLDivElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const headDom = useMeasureNew(headRef.current);
  const bodyDom = useMeasureNew(bodyRef.current);

  const onClickHandler = () => {
    toggleCallback(index);
  };

  const [minHeight, maxHeight] = getRootMinMaxHeight({ headDom, bodyDom, isOpen });

  return (
    <li className={classNames('Accordion-item', { 'is-open': isOpen })} style={{ minHeight, maxHeight }}>
      <div
        ref={(el: HTMLDivElement) => {
          (headRef.current as HTMLDivElement) = el;
        }}
        className="Accordion-head-wrapper"
        aria-label="accordion-head"
        aria-expanded={isOpen}
      >
        <div className={`Accordion-head u-flex u-flexCol`} role="button" onClick={onClickHandler}>
          <div className={`${warningSeverityClass ?? ''}`}>
            <div className={`Accordion-head-main u-flex u-flex-1`}>
              <div className="u-flex-1">{heading}</div>
              <div className="u-flexGrow-0 u-flex u-flexAlignItemsCenter Accordion--expand-icon">
                <IconV1 iconClass="svg--fill-inherit" size={20} icon={`${isOpen ? 'chevron-up' : 'chevron-down'}`} />
              </div>
            </div>
          </div>

          <div className="Accordion-head-additional u-flex-1">{headingAdditional}</div>
        </div>
      </div>
      <div
        ref={(el: HTMLDivElement) => {
          (bodyRef.current as HTMLDivElement) = el;
        }}
        className="Accordion-content-background"
      >
        <div className={'Accordion-content-divider'}>
          <hr />
        </div>
        <div className={'Accordion-content'}>{body}</div>
      </div>
    </li>
  );
};
